<script setup lang="ts">
import { computed, nextTick } from 'vue';
import Vue3DraggableResizable from 'vue3-draggable-resizable';
import { useDisplay } from 'vuetify';
import { storeToRefs } from 'pinia';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faExpand, faCompress, faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons';

library.add(faExpand, faCompress, faUpRightAndDownLeftFromCenter);
import VideoPlayer from './VideoPlayer.vue';
import AudioPlayer from './AudioPlayer.vue';
import useMediaStore from '../stores/media';
import useVideoOptions from '../composables/useVideoOptions';
import useLocale from '../composables/useLocale';
import { Track } from '../types';
import type { EpubJsManager } from '../types/epubjs';
import useAudioTracks from '../composables/use-audio-tracks';
import useReaderEngine from '../engine/use-reader-engine';
import useRenditionStore from '../stores/rendition';
import useReaderStore from '../stores/reader';
import useSettingStore from '../stores/setting';
import useUiStore from '../stores/ui';

defineEmits(['click.stop']);
const { t } = useLocale();
const { width, height } = useDisplay();
const {
  localSize,
  localPosition,
  fullScreen,
  isResizable,
  isDraggable,
  onVideoResize,
  onVideoDrag,
  setVideoProps,
  toggleDraggableResizable,
  fitToAspect,
} = useVideoOptions();
const mediaStore = useMediaStore();
const { currentTrack, isVideoLanguage, hasPictureInPicture, videoSize, videoPosition, currentPageTracks } =
  storeToRefs(mediaStore);

const hasPageVideoTracks = computed(() => currentPageTracks.value.length > 0);
const { getCurrentPages, getIframeByTrack, getViewByTrack, getPageSpineItem } = useReaderEngine();
const { loadContinuousPlaybackTracks, loadNextSpineTracksByHrefAndPlay } = useAudioTracks();
const renditionStore = useRenditionStore();
const { rendition, isReflowable } = storeToRefs(renditionStore);
const { goToPage, setIsChangingPage, goToNextSpine, doWithAutoPageTurnTimeout } = renditionStore;

const readerStore = useReaderStore();
const { isContinuous } = storeToRefs(readerStore);

const { isAutoFollowPlayback } = storeToRefs(useSettingStore());

const { toggleDrawerAndControls } = useUiStore();

const onPipFrameClick = () => {
  toggleDrawerAndControls(true, true);
};

const onFullScreenVideo = async () => {
  fullScreen.value = !fullScreen.value;
  if (fullScreen.value) {
    localSize.value = { w: width.value, h: height.value };
    localPosition.value = { x: 0, y: 0 };
    toggleDraggableResizable(false);
  } else {
    localSize.value = { ...videoSize.value };
    await nextTick();
    localPosition.value = { x: videoPosition.value.x ?? 0, y: videoPosition.value.y ?? 0 };
    if (currentTrack.value) {
      setVideoProps(currentTrack.value);
    }
    toggleDraggableResizable(true);
  }
};

const scrollToPlayingTrack = (track: Track) => {
  const iFrame = getIframeByTrack(track);
  const view = getViewByTrack(track);
  const element = iFrame?.getElementById(track.text.srcFragmentId);

  if (!element || !view || !rendition.value) {
    return;
  }

  // Check if element is a pagebreak or similar non-visual element
  const epubType = element.getAttribute('epub:type');
  if (epubType === 'pagebreak') {
    console.debug(`Skipping scroll to pagebreak element: ${track.text.srcFragmentId}`);
    return;
  }

  const viewOffset = view.offset();
  const elementLocationInView = view?.locationOf(track.text.src);

  // Validate locationOf result before using it
  // If locationOf returns undefined, null, or invalid coordinates, skip scrolling
  // This prevents jumping to incorrect positions when encountering non-renderable elements
  if (
    !elementLocationInView ||
    typeof elementLocationInView.top !== 'number' ||
    !isFinite(elementLocationInView.top) ||
    typeof elementLocationInView.left !== 'number' ||
    !isFinite(elementLocationInView.left)
  ) {
    console.warn(
      `Cannot determine valid position for SMIL target: ${track.text.srcFragmentId} in ${track.text.src}. ` +
        `Maintaining current scroll position. locationOf returned:`,
      elementLocationInView,
    );
    return; // Don't scroll if we can't determine valid position
  }

  // Additional safety check: ensure coordinates are reasonable (not default/zero values from failed lookups)
  // A valid element should have some offset within its view
  if (elementLocationInView.top === 0 && elementLocationInView.left === 0) {
    // Check if element actually has dimensions (is rendered)
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      console.debug(`Element ${track.text.srcFragmentId} has no dimensions, skipping scroll`);
      return;
    }
  }

  // @ts-ignore - plus can be applied to number
  const elementTopOffsetRelativeToTheBook = viewOffset.top + elementLocationInView.top;
  // @ts-ignore - plus can be applied to number
  const elementLeftOffsetRelativeToTheBook = viewOffset.left + elementLocationInView.left;

  const bottomOfPageRelativeToTheBook = (rendition.value?.manager.scrollTop ?? 0) + window.innerHeight;

  const bottomControlsHeight = 96;

  const isElementNotVisible = elementTopOffsetRelativeToTheBook > bottomOfPageRelativeToTheBook - bottomControlsHeight;

  if (isElementNotVisible) {
    const scrollingPadding = 64;
    const scrollTopOffset = elementTopOffsetRelativeToTheBook - scrollingPadding;

    const scrollContainer = rendition.value?.manager.container as HTMLElement;
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollTopOffset,
        left: elementLeftOffsetRelativeToTheBook,
        behavior: 'smooth',
      });
    } else if (rendition.value?.manager) {
      (rendition.value.manager as EpubJsManager).scrollTo(elementLeftOffsetRelativeToTheBook, scrollTopOffset, true);
    }
  }
};

const followReader = (track: Track): void => {
  if (!isContinuous.value) {
    // This feature supports only continuous flow.
    return;
  }
  if (!getCurrentPages().find((p) => p.path === track.text.srcFile)) {
    navigateToTrackPage(track);
  } else {
    scrollToPlayingTrack(track);
  }
};

const navigateToTrackPage = (track: Track): void => {
  goToPage({
    target: track.text.src,
    isGoingForwardsInBook: true,
  });
  // Reset flag, that was set in goToPage method.
  setIsChangingPage(false);
};

// Handle AudioPlayer events - maintain existing Reader behavior
const handleNextTrackOnPage = (track: Track) => {
  if (!isContinuous.value) {
    followReader(track);
    return;
  }

  // Avoid auto-scroll if auto follow playback is disabled.
  if (!isAutoFollowPlayback.value) return;

  // For continuous mode: Check if playing chapter is within the active virtualization zone
  // Uses the same 5-viewport buffer logic as the virtual scroller
  try {
    const spineItem = getPageSpineItem(track.cfi || track.text.src);
    const manager = rendition.value?.manager as any;

    if (manager && typeof manager.isViewInActiveZone === 'function') {
      const isInActiveZone = manager.isViewInActiveZone(spineItem.index);

      if (isInActiveZone) {
        // Playing chapter is within the active zone - follow along for text highlighting
        followReader(track);
      } else {
        // User has scrolled far away (outside 5-viewport buffer) - don't interrupt
        console.debug('Skipping auto-scroll: playing chapter outside active zone');
      }
    } else {
      // Fallback: if method doesn't exist, use old behavior
      const trackIsOnVisiblePage = getCurrentPages().find((p) => p.path === track.text.srcFile);
      if (trackIsOnVisiblePage) {
        followReader(track);
      }
    }
  } catch (error) {
    console.warn('Error checking active zone:', error);
    // Fallback on error
    followReader(track);
  }
};

// Pause after the last track on a page ends before sliding to the next page,
// so the user isn't rushed straight into the transition.
const AUTO_PAGE_TURN_DELAY_MS = 2000;

const handleGoToNextSpine = () => {
  if (isContinuous.value || isReflowable.value) {
    goToNextSpine();
    return;
  }
  doWithAutoPageTurnTimeout(() => goToNextSpine(), AUTO_PAGE_TURN_DELAY_MS);
};

const handleLoadContinuousPlayback = async (track: Track) => {
  await loadContinuousPlaybackTracks(track);
};

const handleLoadNextSpineTracks = (href: string) => {
  loadNextSpineTracksByHrefAndPlay(href);
};
</script>

<template>
  <div v-if="isVideoLanguage && hasPageVideoTracks" class="video-frame">
    <Vue3DraggableResizable
      v-if="hasPictureInPicture"
      v-model:x="localPosition.x"
      v-model:y="localPosition.y"
      v-model:w="localSize.w"
      v-model:h="localSize.h"
      :init-w="localSize.w"
      :init-h="localSize.h"
      :class-name="fullScreen ? 'floating-video full-screen' : 'floating-video'"
      class-name-dragging="floating-video--dragging"
      class-name-resizing="floating-video--resizing"
      class-name-active="floating-video--active"
      :draggable="isDraggable"
      :resizable="isResizable"
      :handles="['br']"
      :parent="true"
      @drag-end="onVideoDrag"
      @resize-end="onVideoResize"
    >
      <div
        class="pip-frame"
        role="button"
        tabindex="0"
        :aria-label="t('label_open_reader_controls')"
        @click="onPipFrameClick"
        @keydown.enter.space.prevent="onPipFrameClick"
      >
        <VideoPlayer @setVideoProps="setVideoProps" @video-aspect="fitToAspect" />
      </div>
      <div v-if="!fullScreen" class="resize-grip" aria-hidden="true">
        <FontAwesomeIcon :icon="['fas', 'up-right-and-down-left-from-center']" />
      </div>
      <v-btn
        variant="text"
        :ripple="false"
        class="video-frame-btn video-fullscreen-toggle"
        :class="fullScreen ? 'is-fullscreen' : 'is-pip'"
        :title="fullScreen ? t('button_full_screen_exit') : t('button_full_screen')"
        :aria-label="fullScreen ? t('button_full_screen_exit') : t('button_full_screen')"
        :aria-pressed="fullScreen"
        @click.stop="onFullScreenVideo()"
      >
        <FontAwesomeIcon :icon="fullScreen ? 'fas fa-compress' : 'fas fa-expand'" />
      </v-btn>
    </Vue3DraggableResizable>
    <VideoPlayer v-else @setVideoProps="setVideoProps" />
  </div>
  <AudioPlayer
    v-else
    @next-track-on-page="handleNextTrackOnPage"
    @go-to-next-spine="handleGoToNextSpine"
    @load-continuous-playback="handleLoadContinuousPlayback"
    @load-next-spine-tracks="handleLoadNextSpineTracks"
  />
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.video-frame {
  min-width: 100vw;
  min-height: 100vh;
  min-height: 100dvh; //for mobile browsers
  position: absolute;
  top: 0;
  left: 0;
  border: 1px solid #000;
  user-select: none;
  pointer-events: none;
}
.pip-frame {
  position: absolute;
  inset: 8px;
  background-color: rgb(var(--v-theme-inverse-surface), 0.9);
  border: 1px solid rgb(var(--v-theme-outline-variant));
  outline: solid 1px rgb(var(--v-theme-outline-variant)) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  box-sizing: border-box;
  pointer-events: auto;
  cursor: grab;

  // Keyboard focus indicator — the .pip-frame is role=button + tabindex=0, so it shows up
  // in the Tab order. focus-visible avoids the ring on mouse clicks.
  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary)) !important;
    outline-offset: 2px;
  }

  // Override VideoPlayer's solid-surface bg to transparent in PIP — chip backdrop shows through.
  :deep(.video-player-overlay) {
    background-color: transparent;
  }
}

:deep(.floating-video) {
  cursor: grab;
  pointer-events: auto;
}
:deep(.floating-video--dragging) {
  cursor: grabbing;
}
:deep(.floating-video--dragging) .pip-frame {
  cursor: grabbing;
}

:deep(.vdr-handle-br) {
  display: block !important;
  pointer-events: auto;
  background: transparent;
  border: 0;
  width: 44px;
  height: 44px;
  right: -12px;
  bottom: -12px;
  cursor: nwse-resize;
}
// Note: the library renders .vdr-handle-br as a plain <div> with no role/tabindex, so AT
// won't surface it. The visible .resize-grip carries `aria-hidden="true"` in the template
// for the same reason — neither chip nor handle should be announced; only the .pip-frame
// (role=button) and the fullscreen v-btn are real focus targets.

:deep(.floating-video.full-screen .vdr-handle-br) {
  display: none !important;
}

.video-frame-btn,
.resize-grip {
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgb(var(--v-theme-inverse-surface), 0.9) !important;
  border: 1px solid rgb(var(--v-theme-outline-variant)) !important;
  border-radius: 50% !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  color: rgb(var(--v-theme-surface));
  z-index: 10;
  transition:
    background-color 0.15s ease,
    transform 0.15s ease;

  :deep(svg) {
    font-size: 20px;
  }
}

.video-frame-btn {
  position: absolute;
  pointer-events: auto;
  z-index: 8;

  &.is-pip {
    right: -12px;
    bottom: 36px;
  }

  &.is-fullscreen {
    right: 64px;
    bottom: 160px;
    @media #{map.get(v.$display-breakpoints, 'md-and-down')} {
      right: 30px;
    }
  }

  :deep(svg) {
    color: rgb(var(--v-theme-surface)) !important;
  }

  &:hover,
  &:focus-visible {
    background-color: rgb(var(--v-theme-inverse-surface), 1) !important;
    transform: scale(1.05);
  }
}

.resize-grip {
  position: absolute;
  right: -12px;
  bottom: -12px;
  pointer-events: none;

  svg {
    font-size: 18px;
    transform: rotate(90deg);
  }
}
:deep(.floating-video):has(.vdr-handle-br:hover) .resize-grip,
:deep(.floating-video--resizing) .resize-grip {
  background-color: rgb(var(--v-theme-inverse-surface), 1) !important;
  transform: scale(1.05);
}
</style>
